import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CardContent,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  Tooltip,
} from '@cambridgeassessment/cambridge-ui';
import { Box, Switch, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { COUNTRY_CODE_US, REPORT_MAKER_ROLES, Roles } from 'src/constants';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import useOrgAppRoleCollapse from 'src/hooks/useOrgAppRoleCollapse';
import useOrgAppRoles from 'src/hooks/useOrgAppRoles';
import useOrgSshAdmins from 'src/hooks/useOrgSshAdmins';
import useOrgUserDetails from 'src/hooks/useOrgUserDetails';
import { AppRoleInfo, RoleInfo } from 'src/types';
import CancelButtonDialog from '../CancelButtonDialog/CancelButtonDialog';
import Loader from '../Loader/Loader';
import CustomNotification from '../Notifications/CustomNotification';
import OrgSshUserDialog from '../OrgSshUserDialog/OrgSshUserDialog';
import { routes } from '../Pages';
import SearchField from '../SearchField/SearchField';
import NavConfirmation from './NavConfirmation';
import { useStyles } from './styles';
import { arrayIsEqual, rolesToPayload } from './utils';

// eslint-disable-next-line @typescript-eslint/ban-types
type Props = {
  disabled: boolean;
};
let sharedRolesStats = {
  added: 0,
  removed: 0,
  sharedRolesAdded: [],
  sharedRolesRemoved: [],
};
const OrgUserApplicatonRoles: React.FC<Props> = ({ disabled }) => {
  const { UID, associateId } = useParams();
  const {
    orgAppRoles,
    isLoading,
    searchQuery,
    filteredAppRoles,
    updateExpand,
    updateSearchQuery,
  } = useOrgAppRoles(UID, associateId);
  const {
    userDetails,
    updateUserRoles,
    showNotification,
    updateSshCoordinatorRole,
  } = useOrgUserDetails();
  const { addNotification } = useNotifications();
  const history = useHistory();

  const [roles, setRoles] = useState(userDetails.roles);
  const { admins } = useOrgSshAdmins(associateId);
  const [showSshDialog, setShowSshDialog] = useState(false);
  const [showSshDialogError, setShowSshDialogError] = useState(false);
  const [reportMakerCountryError, setReportMakerCountryError] = useState(false);
  const { user } = useAuth();

  const classes = useStyles();
  if (arrayIsEqual(roles, userDetails.roles))
    sharedRolesStats = {
      added: 0,
      removed: 0,
      sharedRolesAdded: [],
      sharedRolesRemoved: [],
    };

  const { collapseAll, setCollapseAll } =
    useOrgAppRoleCollapse(filteredAppRoles);

  const sharedRole = (roleName: string) => {
    return []
      .concat(...filteredAppRoles.appRoles.map((ele) => ele.roles))
      .find((item) => item.isShared && item.name === roleName);
  };

  const getSharedRolesPlatforms = useCallback(
    (sharedRoles) => {
      return filteredAppRoles.appRoles
        .filter((ele) => {
          return ele.roles.some((role) => sharedRoles.includes(role.name));
        })
        .map((appRole) => appRole.platform);
    },
    [filteredAppRoles.appRoles]
  );

  const setSharedRolesStats = (isChecked: boolean, roleName: string) => {
    const isRoleAssigned = userDetails.roles.indexOf(roleName) >= 0;
    if (isChecked && !isAssignedRole(roleName)) {
      !isRoleAssigned
        ? (sharedRolesStats.added++,
          sharedRolesStats.sharedRolesAdded.push(roleName))
        : (sharedRolesStats.removed--,
          (sharedRolesStats.sharedRolesRemoved =
            sharedRolesStats.sharedRolesRemoved.filter(
              (item) => item !== roleName
            )));
    }
    if (!isChecked && isAssignedRole(roleName)) {
      !isRoleAssigned
        ? (sharedRolesStats.added--,
          (sharedRolesStats.sharedRolesAdded =
            sharedRolesStats.sharedRolesAdded.filter(
              (item) => item !== roleName
            )))
        : (sharedRolesStats.removed++,
          sharedRolesStats.sharedRolesRemoved.push(roleName));
    }
  };

  const handleToggle =
    (roleName: string, key: number) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;

      if (isChecked && !isAssignedRole(roleName)) {
        setRoles([...roles, roleName]);
      }

      if (!isChecked && isAssignedRole(roleName)) {
        setRoles(roles.filter((role) => role !== roleName));
      }

      if (sharedRole(roleName)) {
        setSharedRolesStats(isChecked, roleName);
      }
      updateExpand(false, false, key);
    };

  const handleSave = () => {
    if (
      admins.length >= 1 &&
      roles.includes(Roles.SCHOOL_SUPPORT_HUB_COORDINATOR)
    ) {
      setShowSshDialog(true);
      return;
    }

    if (
      REPORT_MAKER_ROLES.some((role) => roles.includes(role)) &&
      user.country !== COUNTRY_CODE_US
    ) {
      setReportMakerCountryError(true);
      return;
    }

    updateUserRoles(rolesToPayload(roles));
  };

  const handleSshProceed = async () => {
    setShowSshDialog(false);

    try {
      const loggedInUID = user.id;
      const existingAdmins = admins.filter(
        (admin) => admin.UID !== userDetails.UID
      );
      if (existingAdmins.some((admin) => admin.UID === loggedInUID)) {
        setShowSshDialogError(true);
        throw new Error();
      }

      if (existingAdmins.length > 0) {
        await updateSshCoordinatorRole(existingAdmins);
      }

      await updateUserRoles(rolesToPayload(roles));
    } catch (error) {
      addNotification({
        title: 'Error updating roles',
        variant: 'error',
      });
    }
  };

  const prompt = () => history.push(routes.MyOrganisation.path);

  const handleNoSave = () => setRoles(userDetails.roles);

  const onCollapseAll = useCallback(() => {
    setCollapseAll((prev) => !prev);
    updateExpand(true, collapseAll, null);
  }, [updateExpand, collapseAll]);

  const isAssignedRole = (roleName: string) => roles.indexOf(roleName) >= 0;

  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  const sharedRoleModelTitle = () => {
    if (sharedRolesStats.added && sharedRolesStats.removed) {
      return 'Add/Remove shared role?';
    }
    return sharedRolesStats.added ? 'Add shared role?' : 'Remove shared role?';
  };

  const sharedRoleModelContent = () => {
    const modelContent =
      'You have {{ACTION}} a shared role. This role is used by multiple services, are you sure you want to continue?';
    if (sharedRolesStats.added && sharedRolesStats.removed) {
      return {
        modalContent: modelContent.replace(/{{ACTION}}/g, 'added/removed'),
        platforms: getSharedRolesPlatforms([
          ...sharedRolesStats.sharedRolesAdded,
          ...sharedRolesStats.sharedRolesRemoved,
        ]),
      };
    }
    return {
      modalContent: modelContent.replace(
        /{{ACTION}}/g,
        sharedRolesStats.added ? 'added' : 'removed'
      ),
      platforms: getSharedRolesPlatforms(
        sharedRolesStats.added
          ? sharedRolesStats.sharedRolesAdded
          : sharedRolesStats.sharedRolesRemoved
      ),
    };
  };

  return (
    <Box>
      <NavConfirmation
        when={!arrayIsEqual(roles, userDetails.roles)}
        onConfirm={
          sharedRolesStats.added || sharedRolesStats.removed
            ? openModal
            : () => {
                handleSave();
                prompt();
              }
        }
        onReject={handleNoSave}
        data-testid="nav-confirmation-modal"
      />
      {showNotification && (
        <Box className={classes.notification}>
          <CustomNotification
            variant="info"
            multiline={true}
            title="Your changes were saved, but it takes a couple of minutes for roles to be updated."
            description="There’s no need for you to perform any action. Thank you for your patience."
          />
        </Box>
      )}
      {showSshDialog && (
        <OrgSshUserDialog
          open={showSshDialog}
          title="School Support Hub Coordinator Role"
          message="Note: Only one School Support Hub Coordinator Role can be assigned per centre. Proceeding will assign this role to the current user and remove it from any existing coordinator. Would you like to continue?"
          onCancel={() => setShowSshDialog(false)}
          onProceed={handleSshProceed}
          isError={false}
        />
      )}
      {reportMakerCountryError && (
        <CancelButtonDialog
          open={reportMakerCountryError}
          title="Report Maker"
          message="Sorry, you cannot assign roles related to report maker"
          onCancel={() => setReportMakerCountryError(false)}
        />
      )}
      {showSshDialogError && (
        <OrgSshUserDialog
          open={showSshDialogError}
          title="School Support Hub Coordinator Role"
          message="Error: You are currently assigned as an SSH coordinator. Users cannot modify their own roles while logged in."
          onCancel={() => setShowSshDialogError(false)}
          onProceed={undefined}
          isError={true}
        />
      )}
      <Box
        mt={5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="h4" data-testid="user-roles-title">
            Roles
          </Typography>
        </Box>
        <Box display="inline-flex" alignItems="center">
          <Box mr={1}>
            <Button
              onClick={() => onCollapseAll()}
              data-testid="role-collapse-button"
              style={{ backgroundColor: '#00588f' }}
            >
              {collapseAll ? 'Collapse All' : 'Expand All'}
            </Button>
          </Box>
          <Box>
            <Button
              onClick={
                sharedRolesStats.added || sharedRolesStats.removed
                  ? openModal
                  : handleSave
              }
              data-testid="role-submit-button"
              disabled={
                disabled ? true : arrayIsEqual(roles, userDetails.roles)
              }
              style={{ backgroundColor: '#00588f' }}
            >
              Save changes
            </Button>
            {
              <Modal
                open={open}
                size="small"
                onClose={closeModal}
                data-testid="confirmation-modal"
              >
                <Box className={classes.modalTitle}>
                  <ModalTitle
                    onClose={closeModal}
                    data-testid="confirmation-header"
                  >
                    {sharedRoleModelTitle()}
                  </ModalTitle>
                </Box>
                <ModalContent>
                  <Typography
                    variant="body1"
                    className={classes.modalText}
                    data-testid="confirmation-body"
                  >
                    {sharedRoleModelContent().modalContent}
                  </Typography>
                  <ul>
                    {sharedRoleModelContent().platforms.map((platform) => (
                      <li key={platform}>
                        <Typography
                          variant="body1"
                          data-testid={`list-item-${platform}`}
                        >
                          {platform}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </ModalContent>
                <ModalActions>
                  <Button
                    onClick={closeModal}
                    color="primary"
                    variant="text"
                    data-testid="cancel-confirmation-modal"
                  >
                    Don&apos;t save
                  </Button>
                  <Button
                    onClick={handleSave}
                    color="primary"
                    data-testid="confirm-confirmation-modal"
                  >
                    Save changes
                  </Button>
                </ModalActions>
              </Modal>
            }
          </Box>
        </Box>
      </Box>
      <Box mt={1}>
        <SearchField
          searchQuery={searchQuery}
          placeholder="Search for services or roles"
          onSubmit={updateSearchQuery}
          onChange={updateSearchQuery}
        />
      </Box>
      <Box className={classes.rolesCard}>
        {isLoading ? (
          <CardContent
            key="loader"
            className={classes.item}
            data-testid="loader"
          >
            <Loader height={200} />
          </CardContent>
        ) : (
          filteredAppRoles.appRoles.map((appRole: AppRoleInfo, key) => (
            <Box mt={2} key={`accordion-box-${key}`}>
              <Accordion
                defaultExpanded
                expanded={appRole.expand}
                key={key}
                data-testid={`${appRole.platform}-accordion`}
                onClick={() =>
                  updateExpand(false, appRole.expand, appRole.platform)
                }
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${appRole.platform}-content`}
                  id={`${appRole.platform}-header`}
                  data-testid={`${appRole.platform}-header`}
                >
                  <Box data-testid={`${appRole.platform}-box`}>
                    <Typography
                      variant="subtitle1"
                      data-testid={`${appRole.platform}-typography`}
                    >
                      {appRole.platform}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  data-testid={`${appRole.platform}-accordiondetails`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {appRole.roles.length === 0 ? (
                      <Typography>No roles for application</Typography>
                    ) : (
                      appRole.roles.map((role: RoleInfo, keyId) => (
                        <Box
                          key={keyId}
                          p={1}
                          sx={{ display: 'flex' }}
                          data-testid={`${appRole.platform}-${role.name}-box`}
                        >
                          <Switch
                            size="medium"
                            color="primary"
                            name={role.name}
                            value={role.name}
                            checked={isAssignedRole(role.name)}
                            onChange={handleToggle(role.name, key)}
                            disabled={disabled}
                            data-testid={`${appRole.platform}-${role.name}-switch`}
                          />
                          <Box ml={2}>
                            <Typography
                              variant="subtitle1"
                              data-testid={`${appRole.platform}-${role.alias}-alias`}
                            >
                              {role.alias}&nbsp;&nbsp;
                              {role.isShared && (
                                <Tooltip
                                  border="light"
                                  title={`This role is shared with other services. Selecting / deselecting it here will affect every platform that uses this role.`}
                                  detailed
                                >
                                  <Box
                                    component="label"
                                    className={classes.sharedRole}
                                  >
                                    SHARED ROLE
                                  </Box>
                                </Tooltip>
                              )}
                              &nbsp;&nbsp;
                              {role.internal && (
                                <Tooltip
                                  border="light"
                                  title={`This role is shown to Internal Cambridge staff only and cannot be administered by external customers.`}
                                  detailed
                                >
                                  <Box
                                    component="label"
                                    className={classes.internalRole}
                                  >
                                    INTERNAL ROLE
                                  </Box>
                                </Tooltip>
                              )}
                              {role.restricted && (
                                <Tooltip
                                  border="light"
                                  title={`This role can be applied to any user, but can only be seen and administered by Cambridge internal staff.`}
                                  detailed
                                >
                                  <Box
                                    component="label"
                                    className={classes.restrictedRole}
                                    data-testid={`${appRole.platform}-${role.alias}-restricted-role`}
                                  >
                                    RESTRICTED ROLE
                                  </Box>
                                </Tooltip>
                              )}
                            </Typography>
                            <Typography
                              variant="body1"
                              data-testid={`${appRole.platform}-${role.alias}-description`}
                              style={{
                                fontSize: '1.15em',
                                lineHeight: 1.5,
                              }}
                            >
                              {role.description}
                            </Typography>
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default OrgUserApplicatonRoles;
